import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import Template from '../Components/Template';
import useDataTable from '../Hooks/useDataTable';
import {
  useGetAllExamFormsByAgentsAdminFilterMutation,
  useGetAllExamFormsQuery
} from '../Services/modules/exam';
import {
  MdOutlineNavigateNext, MdSkipNext, MdSkipPrevious, MdEdit, MdDelete
} from "react-icons/md";
import { GrFormPrevious } from "react-icons/gr";
import { Config } from '../Config';
import { useGetAllAgentsQuery } from '../Services/modules/agents';

function ReportAdmin() {
  const userData = useSelector((state) => state.userData.user_data);

  const [startIndex, setStartIndex] = useState("");
  const [endIndex, setEndIndex] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const {
    handleFirstPage,
    handlePageSizeChange,
    handleLastPage,
    page,
    rowsPerPage,
    totalRows,
    setTotalRows,
    setPage,
    setTotalPages,
    totalPages
  } = useDataTable();

  const [examForms, setExamForms] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  // ✅ MAIN QUERY
  const {
    data,
    isSuccess,
    isLoading,
    error,
    refetch
  } = useGetAllExamFormsQuery({ agent_id: agentId, page_number: page, page_size: rowsPerPage });

  // (optional) Filter-by-agent mutation kept as-is
  const [
    GetAllExamFormsByAgentAdminFilter, {
      data: examFormsDataByAgent,
      isSuccess: examFormDataByAgentIsSuccess,
      isLoading: examFormDataByAgentIsLoading,
      isError: examFormDataByAgentIsError,
      error: examFormDataByAgentError
    }
  ] = useGetAllExamFormsByAgentsAdminFilterMutation();

  const {
    data: dataAgents,
    isSuccess: isSuccessAgents,
    isLoading: isLoadingAgents,
    error: errorAgents,
    refetch: refetchAgents
  } = useGetAllAgentsQuery({ page_number: page, page_size: 1000 });

  useEffect(() => {
    if (isSuccess) {
      setTotalRows(data.totalRows);
      setExamForms(data.data);
    }
  }, [data, isSuccess, setTotalRows]);

  // refetch on page change
  useEffect(() => {
    refetch();
  }, [page, refetch]);

  useEffect(() => {
    if (isSuccessAgents) {
      setAgents(dataAgents.data);
    }
  }, [dataAgents, isSuccessAgents]);

  useEffect(() => {
    if (examFormDataByAgentIsSuccess) {
      setExamForms(examFormsDataByAgent.data);
    }
  }, [examFormsDataByAgent, examFormDataByAgentIsSuccess]);

  // ===== EXPORTS (unchanged) =====
  const exportToExcel = () => {
    fetch(`${Config.API_URL_LOCAL}exam/getAllExamFormsExcel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentId })
    })
      .then((response) => {
        if (!response.ok) {
          alert("Already Exported");
          throw new Error('Network response was not ok');
        }
        const filename = response.headers.get('X-File-Name') || 'exported-data.xlsx';
        return response.blob().then(blob => ({ filename, blob }));
      })
      .then(({ filename, blob }) => {
        if (blob.size === 0) throw new Error('Received an empty Excel file');
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
        refetch();
      })
      .catch((error) => console.error('Error exporting to Excel:', error));
  };

  const exportToExcelAgain = () => {
    fetch(`${Config.API_URL_LOCAL}exam/getAllExamFormsExcelAgain`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agentId })
    })
      .then((response) => {
        if (!response.ok) {
          alert("Already Exported");
          throw new Error('Network response was not ok');
        }
        const filename = response.headers.get('X-File-Name') || 'exported-data.xlsx';
        return response.blob().then(blob => ({ filename, blob }));
      })
      .then(({ filename, blob }) => {
        if (blob.size === 0) throw new Error('Received an empty Excel file');
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => console.error('Error exporting to Excel:', error));
  };

  const exportToExcelRange = () => {
    fetch(`${Config.API_URL_LOCAL}exam/getAllExamFormsExcelRange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ startIndex, endIndex }),
    })
      .then((response) => {
        if (!response.ok) {
          alert("Already Exported");
          throw new Error('Network response was not ok');
        }
        const filename = response.headers.get('X-File-Name') || 'exported-data.xlsx';
        return response.blob().then(blob => ({ filename, blob }));
      })
      .then(({ filename, blob }) => {
        if (blob.size === 0) throw new Error('Received an empty Excel file');
        const url = window.URL.createObjectURL(new Blob([blob]));
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => console.error('Error exporting to Excel:', error));
  };

  const handleExportRange = () => {
    if (startIndex && endIndex) {
      exportToExcelRange();
    } else {
      alert("Please enter both start and end index.");
    }
  };

  // ✅ UI-ONLY SEQUENTIAL INDEX
  // This is the only logic you need to show a perfect 1..N sequence across pages.
  const displayStartIndex = page * rowsPerPage + 1;

  const ReportAdminScreen = () => {
    return (
      <>
        <main id="main" className="main">
          <div className='container'>
            <div className='card'>
              <div className='card-body'>

                <div className="row" style={{ marginBottom: "20px" }}></div>

                <button onClick={exportToExcel}>Export To Excel</button>
                <button onClick={exportToExcelAgain} style={{ marginLeft: 8 }}>Export To Excel Again</button>

                <div className="index-range" style={{ marginTop: 12, marginBottom: 12 }}>
                  <input
                    type="number"
                    placeholder="Start Index"
                    value={startIndex}
                    onChange={(e) => setStartIndex(e.target.value)}
                    style={{ marginRight: 8 }}
                  />
                  <input
                    type="number"
                    placeholder="End Index"
                    value={endIndex}
                    onChange={(e) => setEndIndex(e.target.value)}
                    style={{ marginRight: 8 }}
                  />
                  <button onClick={handleExportRange}>Export Range</button>
                </div>

                <table className="bordered-table">
                  <thead>
                    <tr>
                      <th>Exported Status</th>
                      <th>#</th> {/* ✅ Continuous display index */}
                      <th>Form ID</th> {/* keep original visible if you want */}
                      <th>Exam</th>
                      <th>Student</th>
                      <th>Standard</th>
                      <th>School Name</th>
                      <th>Form Date</th>
                      <th>Agent Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {examForms.map((item, i) => (
                      <tr key={item._id || i}>
                        <td>
                          {item.is_exported
                            ? <span style={{ color: 'green' }}>&#10004;</span>
                            : <span style={{ color: 'red' }}>&#10008;</span>}
                        </td>

                        {/* ✅ UI-only continuous index */}
                        <td>{displayStartIndex + i}</td>

                        {/* Keep original form_id intact (gaps allowed) */}
                        <td title="Database form_id">{item.form_id}</td>

                        <td>{`${item.exam_name} | ${item.exam_fees} Rs | ${item.exam_medium}`}</td>
                        <td>{`${item.first_name} ${item.middle_name} ${item.last_name} | ${item.gender}`}</td>
                        <td>{item.exam_standard}</td>
                        <td>{item.school_name}</td>
                        <td>{`${item.form_created_date} ${item.form_created_time}`}</td>
                        <td>{item?.agentId?.full_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => handleFirstPage()} disabled={page === 0}><MdSkipPrevious /></button>
                  <button onClick={() => setPage(page - 1)} disabled={page === 0}><GrFormPrevious /></button>
                  <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}><MdOutlineNavigateNext /></button>
                  <button onClick={() => handleLastPage()} disabled={page >= totalPages - 1}><MdSkipNext /></button>

                  <div style={{ marginLeft: 'auto' }}>
                    <select value={rowsPerPage} onChange={handlePageSizeChange}>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </main>
      </>
    )
  };

  return (
    <Template>{ReportAdminScreen()}</Template>
  );
}

export default ReportAdmin;
